import { NgRedux } from '@angular-redux/store';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { AppState } from '../../../store/state.model';
import { base_roles } from './roles';

/** Interface for the portal permissions */
interface Portal {
  name: string;
  isAuthorized: boolean;
}
@Injectable({
  providedIn: 'root'
})
export class FinegrainRolesService {
  /**
   * @property to store customer permissions
   */
  private customerPermissions: object = {};
  /**
   * @property to store custom portal permissions
   */
  private customPermissions: object = {};
  /**
   * @property to store azure coarse grain
   */
  private azureRoles: Array<string>;
  /**
   * @property to store DB fine grain
   */
  private subRoles: Array<string>;
  /**
   * @property to portals supported with its permissions
   */
  private supportedPortals: Array<Portal>;

  constructor(
    public reduxState: NgRedux<AppState>
  ) {
    this.setDefaults();
  }

  /**
   * @function setDefaults sets the values to default
   */
  private setDefaults(): void {
    this.supportedPortals = [
      { name: 'admin', isAuthorized: false },
      { name: 'svpp', isAuthorized: false },
      { name: 'svppv2', isAuthorized: false },
      { name: 'report', isAuthorized: false },
      { name: 'user_role', isAuthorized: false },
      { name: 'dealer_admin', isAuthorized: false },
      { name: '21mm_navi', isAuthorized: false },
      { name: 'vcm', isAuthorized: false },
      { name: 'vcm_admin_plus', isAuthorized: false },
      { name: 'ev_credit_admin', isAuthorized: false },
    ];
  }

  /**
   * @function getSupportedPortals returns the list of portal names supported
   */
  public getSupportedPortals(): Array<string> {
    return this.supportedPortals.map(portal => portal.name);
  }
  /**
   * @function setPermissions sets all permissions after auth is resolved.
   */
  public setPermissions(): void {
    const state = this.reduxState.getState();
    if (!state || !state.auth) {
      // Safe initialization if state is null or missing auth
      this.azureRoles = [];
      this.subRoles = [];
    } else {
      this.azureRoles = state.auth.role || [];
      this.subRoles = state.auth.userAvailableRoles || [];
    }

    this.setPortalPermissions();
    this.setCustomerPermissions();
    this.setCustomPermissions();
  }

  public getUserAvailableRoles(): Array<string> {
    return this.subRoles;
  }

  /**
   * @function withEnvSuffix adds environment suffix to a base role, except for PROD
   * Gets environment suffix dynamically at runtime to support runtime environment detection
   * Converts to UPPERCASE to match Azure AD role naming (e.g., CTP.TSTAdmin-STG)
   */
  private withEnvSuffix(role: string): string {
    const envName = environment.envName;
    if (envName === 'prod') {
      return role;
    }
    const suffix = envName.toUpperCase();
    return `${role}-${suffix}`;
  }

  private hasAnyRole(roles: string[]): boolean {
    const suffixedRoles = roles.map(role => this.withEnvSuffix(role));
    const hasRole = suffixedRoles.some(role => this.subRoles.includes(role));
    return hasRole;
  }

  /**
   * @function setPortalPermissions sets portal permissions.
   */
  private setPortalPermissions(): void {
    this.supportedPortals = [
      {
        name: 'admin',
        isAuthorized: this.hasAnyRole(base_roles.TST_Access)
      },
      {
        name: 'user_role',
        isAuthorized: this.hasAnyRole(base_roles.VCM_AdminPlus)
      },
      {
        name: 'vcm',
        isAuthorized: this.hasAnyRole(base_roles.VCM)
      },
      {
        name: 'vcm_admin_plus',
        isAuthorized: this.hasAnyRole(base_roles.VCM_AdminPlus)
      },
      {
        name: 'ev_credit_admin',
        isAuthorized: this.subRoles.indexOf('EV_CREDIT_ADMIN') > -1
      },
      {
        name: 'report',
        isAuthorized:
          (this.azureRoles.includes('CT-Reports-Preprod') || this.azureRoles.includes('CT-Reports-Prod')) &&
          this.subRoles.includes('REPORTS')
      }
    ];
  }
  /**
   * @function setCustomerPermissions sets customer-level feature permissions
   */
  private setCustomerPermissions(): void {
    const isTSTAdmin: boolean = this.hasAnyRole(base_roles.TSTAdmin);
    this.customerPermissions = {
      'updateEmail': isTSTAdmin,
      'updatePhone': isTSTAdmin,
      'updateAddress': isTSTAdmin,
      'showAdvancedfeatures': isTSTAdmin,
      'isPhoneCapable': isTSTAdmin,
      'showAppfeatures': true,
      'showUserProfile': isTSTAdmin,
      'updateRemoteUser': false,
      'reSyncSettableFlags': false,
      'retryVehicle': false,
    };
  }
  /**
   * @function setCustomPermissions sets component-level custom permissions
   */
  private setCustomPermissions(): void {
    this.customPermissions = {
      'linked-accounts': true,
      'add-vehicle': this.hasAnyRole(base_roles.TSTAdmin) && !environment.production,
      'unlinkVehicle': this.hasAnyRole(base_roles.TSTAdmin),
      'authorizeRemote': this.hasAnyRole(base_roles.RemoteAuth),
      'giveGoodwill': this.hasAnyRole(base_roles.TSTAdmin),
      'retryProvision': this.hasAnyRole(base_roles.TSTAdmin) || (this.subRoles || []).some(role => role === 'OP_ADMIN'),
      'modifyDcm': this.hasAnyRole(base_roles.TSTAdmin) || (this.subRoles || []).some(role => role === 'SUPER_USER')
    };
  }

  /**
   * @function getCustomerPermissions returns a customer-level feature permission
   */
  public getCustomerPermissions(feature: string): boolean {
    return this.customerPermissions[feature];
  }

  /**
   * @function getPortalPermissions returns whether a given portal is authorized
   */
  public getPortalPermissions(portalName: string): boolean {
    const portal = this.supportedPortals.find(p => p.name === portalName);
    return portal ? portal.isAuthorized : false;
  }

  /**
   * @function getCustomPermissions returns a component-level custom permission
   */
  public getCustomPermissions(component: string): boolean {
    return this.customPermissions[component] || false;
  }
}